'use client';
import React, { useEffect, useRef, useState } from 'react';

export default function WhyUs() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  const reasons = [
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Fast Approvals',
      desc: 'Streamlined processes and strong lender relationships enable us to secure approvals faster than traditional channels.',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: 'Expert Team',
      desc: 'Our team of seasoned financial professionals brings decades of combined expertise across all aspects of commercial finance.',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064" />
        </svg>
      ),
      title: 'Wide Network',
      desc: 'Access to hundreds of lenders, investors, and financial institutions nationwide ensures you get the best possible terms.',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Trusted & Compliant',
      desc: 'Fully licensed and regulated, we operate with the highest standards of integrity, transparency, and regulatory compliance.',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Competitive Rates',
      desc: 'Our negotiating power and market knowledge consistently deliver below-market rates and superior loan structures.',
    },
    {
      icon: (
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      title: '24/7 Support',
      desc: 'Dedicated client support ensures you always have access to expert guidance whenever you need it most.',
    },
  ];

  return (
    <section
      className="w-full py-20 relative"
      style={{
        backgroundImage: "url('/assets/images/fietzfotos-stairs-6133971-1786977535092.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Dark overlay */}
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(135deg, rgba(5,15,40,0.88) 0%, rgba(10,31,92,0.82) 50%, rgba(5,15,40,0.88) 100%)' }}
      />
      <div className="max-w-7xl mx-auto px-6 relative" style={{ zIndex: 1 }}>
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl font-medium text-white mb-4">
            Why Choose Grid Global
          </h2>
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-16 bg-brand-green" />
            <div className="w-2 h-2 rounded-full bg-brand-green" />
            <div className="h-px w-16 bg-brand-green" />
          </div>
          <p className="font-sans text-gray-400 text-lg max-w-2xl mx-auto">
            We combine expertise, technology, and relationships to deliver superior financial outcomes
          </p>
        </div>

        {/* Grid */}
        <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons?.map((reason, index) => (
            <div
              key={reason?.title}
              className={`group p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 hover:border-brand-blue/40 transition-all duration-300 hover:-translate-y-1 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms`, transitionDuration: '600ms' }}
            >
              <div className="w-14 h-14 rounded-xl bg-brand-green/20 flex items-center justify-center text-brand-green mb-5 group-hover:bg-brand-green group-hover:text-white transition-all duration-300">
                {reason?.icon}
              </div>
              <h3 className="font-serif text-xl font-semibold text-white mb-3">{reason?.title}</h3>
              <p className="font-sans text-gray-400 text-sm leading-relaxed">{reason?.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
